import { MatchStatus } from "../matchState";

/**
 * AB#38824: whether the create-new patient/requestor form may carry the AI-extracted values
 * forward as its pre-fill.
 *
 * THE RULE (QA, AB#38824): "the only time we want to carry the extracted info forward is when
 * the AI did NOT find a match in the DB." With a match on the row, the specialist choosing to
 * add a NEW person is deliberately entering somebody the database does not hold - so the form
 * must open empty rather than make them clear the matched person's details out of it first.
 */

/** The two signals that decide it. Both rows report both on every render. */
export interface CreatePrefillState {
    readonly status: MatchStatus;
    /** `Boolean(created)` on both rows - the specialist entered this value, the AI did not match it. */
    readonly userEntered: boolean;
}

/**
 * True when a DB match is on the row right now.
 *
 * `matched` is the only MatchStatus meaning "the search ran and returned at least one
 * candidate": deriveStatus returns it exactly when confidence !== "none", after
 * serviceUnavailable / isSearching / awaitingDocument have each been ruled out
 * (matchState.ts). But it is not sufficient on its own -
 *
 * WHY `userEntered` IS PART OF THE TEST. After the specialist saves a new person, both rows
 * SYNTHESIZE a matched result to resolve the row - PatientInfoRow.onCreateSubmit sets
 * `{ matched: true, matchCount: 1, confidence: "Single" }`, RequestorInfoRow.onCreateSubmit
 * does the same through applyApiResult. That is a display device, not a database match: no
 * such record exists yet, and the transaction's final Save is what will write it. Reading
 * status alone would treat it as one and blank the form on the [Edit] -> create path straight
 * after a create. `userEntered` is `Boolean(created)` on both rows and is documented as exactly
 * this distinction ("the specialist created/entered this value rather than the AI matching it",
 * matchState.ts), so it is the signal that already exists for the question rather than a new
 * carve-out invented for this fix.
 */
export function hasDbMatch(state: CreatePrefillState): boolean {
    return state.status === "matched" && !state.userEntered;
}

/**
 * Per status, with the ambiguous ones argued rather than assumed:
 *
 *   matched, AI          -> EMPTY. Single AND multiple. QA reported a single match on both
 *                           rows; a multiple result is no less "the AI found a match in the
 *                           DB" - candidates came back, the specialist looked at them and
 *                           chose to enter somebody else.
 *   matched, user-entered -> PRE-FILL. Not a DB match at all (see hasDbMatch).
 *   noMatch              -> PRE-FILL. The AC9 no-match path (Spec #34101 / F1c) the pre-fill
 *                           was built for, preserved exactly.
 *   serviceUnavailable   -> PRE-FILL. The search never completed, so nothing was found.
 *                           Treating an outage as "found a match" would withdraw the AC9
 *                           convenience precisely when the specialist has least help, and the
 *                           AB#35068 tests pin the pre-fill in this state.
 *   awaitingDocument     -> PRE-FILL, vacuously: no document, so no extracted values to carry.
 *   matching             -> PRE-FILL. Not vacuous, and worth stating plainly: on the PATIENT row
 *                           no create affordance is rendered while a search is in flight, but the
 *                           REQUESTOR row's door is a counter the parent bumps (createOpenSignal,
 *                           from Classic's own results grid), so the form can be opened mid-search
 *                           and will carry the document's values. That is the right default - the
 *                           search has not come back, so no match has been found yet, and the
 *                           alternative is to blank a form on the strength of a result nobody has
 *                           seen. It does mean a match landing a moment later does not retract the
 *                           pre-fill; the values are frozen when the form opens, by design.
 */
export function shouldPrefillCreateForm(state: CreatePrefillState): boolean {
    return !hasDbMatch(state);
}

/**
 * AB#38938: whether the Add New CONTACT form may carry the document's extracted contact forward.
 *
 * The extracted contact describes the requestor the DOCUMENT names. It is wrong to carry it forward
 * when the requestor now on the row is not that one, and that is exactly the case QA reported: the
 * AI matched a requestor, the specialist declined the match and entered a new requestor, so the
 * document's contact belongs to the organisation they rejected.
 *
 * `contactFromDocument` is recorded on the requestor resolution when the requestor is created
 * (DataEntryDetails.onCreateRequestor). ABSENT means "not that case", and absent covers every other
 * route deliberately:
 *
 *   selected / auto-matched  -> PRE-FILL. The AI found that requestor FROM the document's own fax
 *                              or email, so the document's contact is that requestor's contact.
 *   created off a no-match   -> PRE-FILL. Nothing was matched, so there is no rejected
 *                              organisation for the contact to belong to.
 *   picked in Classic's grid -> PRE-FILL. Same as selected; the search keys came from the document.
 *   nothing resolved yet     -> PRE-FILL. No earlier requestor for the values to be stale against.
 *
 * Compared against `false` rather than checked for truthiness so that "absent" and "true" behave
 * identically - only the create path ever states it, and a future caller that forgets to set it
 * gets the pre-existing behaviour rather than a silently blank form.
 */
export function shouldPrefillContactForm(
    resolution?: { readonly contactFromDocument?: boolean },
): boolean {
    return resolution?.contactFromDocument !== false;
}
